// Public blog feed (GET /blog). Read-only, unauthenticated — only
// published posts are returned by the API.

import { useEffect, useState } from "react";

import { api, ApiError } from "@/lib/api";

export interface BlogListItem {
  id: string;
  slug: string;
  locale: string;
  title: string;
  excerpt?: string | null;
  cover_image_url?: string | null;
  published_at?: string | null;
}

export interface BlogPost extends BlogListItem {
  body_html: string;
}

export function useBlogPosts(locale: string): {
  loading: boolean;
  posts: BlogListItem[];
} {
  const [state, setState] = useState<{ loading: boolean; posts: BlogListItem[] }>({
    loading: true,
    posts: [],
  });
  useEffect(() => {
    let alive = true;
    setState({ loading: true, posts: [] });
    api<BlogListItem[]>(`/blog?locale=${encodeURIComponent(locale)}`)
      .then((posts) => alive && setState({ loading: false, posts }))
      .catch(() => alive && setState({ loading: false, posts: [] }));
    return () => {
      alive = false;
    };
  }, [locale]);
  return state;
}

export function useBlogPost(slug: string): {
  loading: boolean;
  post: BlogPost | null;
  notFound: boolean;
} {
  const [state, setState] = useState<{
    loading: boolean;
    post: BlogPost | null;
    notFound: boolean;
  }>({ loading: true, post: null, notFound: false });
  useEffect(() => {
    let alive = true;
    setState({ loading: true, post: null, notFound: false });
    api<BlogPost>(`/blog/${encodeURIComponent(slug)}`)
      .then((post) => alive && setState({ loading: false, post, notFound: false }))
      .catch(
        (e) =>
          alive &&
          setState({
            loading: false,
            post: null,
            notFound: e instanceof ApiError && e.status === 404,
          }),
      );
    return () => {
      alive = false;
    };
  }, [slug]);
  return state;
}
